import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";

import { useCloseIssueDetailModal, useInspectIssueIds } from "./hooks";
import BusinessSummaryColumn from "./BusinessSummaryColumn";
import MonitorTimeline from "../MonitorTimeline/MonitorTimeline";
import { useTrueBizApi } from "../../../api";

export default function MonitorDetailModal() {
  const { issueId, monitorDomain: providedMonitorDomain } =
    useInspectIssueIds();
  const close = useCloseIssueDetailModal();

  const api = useTrueBizApi();

  const monitorQuery = useQuery({
    queryKey: ["getMonitor", providedMonitorDomain],
    queryFn: async () => {
      let monitorDomain: string;

      if (!providedMonitorDomain) {
        if (!issueId) {
          return null;
        }
        monitorDomain = (await api.getAnomaly(issueId)).domain;
      } else {
        monitorDomain = providedMonitorDomain;
      }

      return await api.getMonitoringEnrollment(monitorDomain);
    },
    throwOnError: true,
  });

  // there are instances where we need to launch the modal without the
  // monitor domain and just an anomaly id (i.e. from the webhooks api)
  const monitorDomain = providedMonitorDomain || monitorQuery?.data?.domain;

  const wprQuery = useQuery({
    queryKey: ["getCompanyLookup", monitorDomain],
    queryFn: () =>
      api.getCompanyLookup({ searchId: "most_recent", domain: monitorDomain }),
    retry: false,
    throwOnError: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!monitorDomain, // this will error if we pass "most_recent" without a domain
  });

  const monitor = monitorQuery.data || null;
  const company = wprQuery.data?.response_data || null;

  return (
    <Dialog
      open={issueId != null || monitorDomain != null}
      onClose={() => {
        close();
      }}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      keepMounted={false}
      slotProps={{
        paper: {
          sx: {
            height: "90vh",
            minWidth: 1000,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0, minHeight: "100%" }}>
        <IconButton
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 99,
          }}
          onClick={() => {
            close();
          }}
        >
          <CloseIcon />
        </IconButton>
        {monitorQuery.isLoading || wprQuery.isLoading ? (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Box
            sx={{
              display: "flex",
              minHeight: "100%",
            }}
          >
            <Box
              sx={{
                flexBasis: "30%",
                minHeight: "100%",
                MaxWidth: "30%",
              }}
            >
              {monitorDomain && (
                <BusinessSummaryColumn
                  domain={monitorDomain}
                  company={company && !("message" in company) ? company : null}
                />
              )}
            </Box>
            <Box
              sx={{
                flexBasis: "70%",
                maxWidth: "70%",
                height: "100%",
              }}
            >
              {monitor && (
                <MonitorTimeline
                  monitor={monitor}
                  focusedNotificationId={issueId}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
